import { useMemo, useState, type Dispatch, type SetStateAction } from "react";
import { useSearchParams } from "react-router-dom";
import PageHero from "@/components/feature/PageHero";
import WorkCard from "@/components/feature/WorkCard";
import EmptyState from "@/components/base/EmptyState";
import { worksList, workCategories, workRegions, workYears } from "@/mocks/works";

const INITIAL_COUNT = 8;
const STEP = 4;

function toggle(list: string[], value: string): string[] {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}

export default function RecommendList() {
  const [searchParams] = useSearchParams();
  const initialCategory = searchParams.get("category");

  const [categories, setCategories] = useState<string[]>(initialCategory ? [initialCategory] : []);
  const [regions, setRegions] = useState<string[]>([]);
  const [years, setYears] = useState<string[]>([]);
  const [sort, setSort] = useState<"hot" | "new">("hot");
  const [visible, setVisible] = useState(INITIAL_COUNT);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const filtered = useMemo(() => {
    let list = [...worksList];
    if (categories.length) list = list.filter((w) => categories.includes(w.category));
    if (regions.length) list = list.filter((w) => regions.includes(w.region));
    if (years.length) list = list.filter((w) => years.includes(String(w.year)));
    list.sort((a, b) => (sort === "hot" ? b.rating - a.rating : b.year - a.year));
    return list;
  }, [categories, regions, years, sort]);

  const visibleItems = filtered.slice(0, visible);
  const hasMore = visible < filtered.length;
  const hasFilter = categories.length > 0 || regions.length > 0 || years.length > 0;

  const reset = () => {
    setCategories([]);
    setRegions([]);
    setYears([]);
    setSort("hot");
    setVisible(INITIAL_COUNT);
  };

  const onToggle = (setter: Dispatch<SetStateAction<string[]>>, value: string) => {
    setter((prev) => toggle(prev, value));
    setVisible(INITIAL_COUNT);
  };

  const Chip = ({
    active,
    label,
    onClick,
  }: {
    active: boolean;
    label: string;
    onClick: () => void;
  }) => (
    <button
      type="button"
      onClick={onClick}
      className={`cursor-pointer whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${
        active ? "bg-accent-500 text-white" : "bg-secondary-100 text-secondary-800 hover:bg-secondary-200"
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="w-full">
      <PageHero
        title="动漫作品推荐"
        description="按类型、地区、年代多维筛选，发现适合你的优质动漫作品，并查看详细的观看指南。"
        crumbs={[{ label: "动漫作品推荐" }]}
        icon="ri-movie-2-line"
      />

      <section className="mx-auto max-w-[1280px] px-4 py-8 md:px-8 md:py-10">
        <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
          <div className="flex items-center justify-between md:hidden">
            <button
              type="button"
              onClick={() => setFiltersOpen((v) => !v)}
              className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-secondary-100 px-3.5 py-2 text-sm font-medium text-secondary-800"
            >
              <i className="ri-filter-3-line text-base" />
              筛选条件
              <i className={filtersOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} />
            </button>
            <span className="text-sm text-foreground-500">
              共 <strong className="text-foreground-900">{filtered.length}</strong> 部
            </span>
          </div>

          <div className={`${filtersOpen ? "mt-4 block" : "hidden"} md:mt-0 md:block`}>
            <div className="flex flex-col gap-4 md:flex-row md:items-start md:gap-6">
              <div className="flex flex-1 flex-col gap-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="w-12 shrink-0 text-sm font-medium text-foreground-600">类型</span>
                  {workCategories.map((c) => (
                    <Chip
                      key={c}
                      active={categories.includes(c)}
                      label={c}
                      onClick={() => onToggle(setCategories, c)}
                    />
                  ))}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="w-12 shrink-0 text-sm font-medium text-foreground-600">地区</span>
                  {workRegions.map((r) => (
                    <Chip key={r} active={regions.includes(r)} label={r} onClick={() => onToggle(setRegions, r)} />
                  ))}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="w-12 shrink-0 text-sm font-medium text-foreground-600">年代</span>
                  {workYears.map((y) => (
                    <Chip
                      key={y}
                      active={years.includes(String(y))}
                      label={`${y}`}
                      onClick={() => onToggle(setYears, String(y))}
                    />
                  ))}
                </div>
              </div>

              <div className="flex shrink-0 flex-col gap-2 md:border-l md:border-background-200 md:pl-6">
                <span className="text-sm font-medium text-foreground-600">排序</span>
                <div className="flex items-center gap-1 rounded-full bg-secondary-100 p-1">
                  <button
                    type="button"
                    onClick={() => setSort("hot")}
                    className={`cursor-pointer whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${
                      sort === "hot" ? "bg-primary-500 text-white" : "text-secondary-800 hover:bg-secondary-200"
                    }`}
                  >
                    人气
                  </button>
                  <button
                    type="button"
                    onClick={() => setSort("new")}
                    className={`cursor-pointer whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors ${
                      sort === "new" ? "bg-primary-500 text-white" : "text-secondary-800 hover:bg-secondary-200"
                    }`}
                  >
                    更新时间
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-5 hidden items-center justify-between text-sm text-foreground-500 md:flex">
          <span>
            共 <strong className="text-foreground-900">{filtered.length}</strong> 部作品
          </span>
          {hasFilter && (
            <button
              type="button"
              onClick={reset}
              className="flex cursor-pointer items-center gap-1 text-accent-600 hover:text-accent-700"
            >
              <i className="ri-refresh-line" />
              重置筛选
            </button>
          )}
        </div>

        {filtered.length > 0 ? (
          <>
            <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-5">
              {visibleItems.map((item) => (
                <WorkCard key={item.id} item={item} />
              ))}
            </div>

            <div className="mt-8 flex flex-col items-center gap-3">
              {hasMore ? (
                <button
                  type="button"
                  onClick={() => setVisible((v) => v + STEP)}
                  className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-accent-600"
                >
                  <i className="ri-arrow-down-line text-base" />
                  加载更多作品
                </button>
              ) : (
                <p className="text-sm text-foreground-400">已加载全部 {filtered.length} 部作品</p>
              )}
              {hasFilter && (
                <button
                  type="button"
                  onClick={reset}
                  className="cursor-pointer text-sm text-foreground-500 underline-offset-4 hover:text-accent-600 hover:underline md:hidden"
                >
                  重置筛选条件
                </button>
              )}
            </div>
          </>
        ) : (
          <div className="mt-5">
            <EmptyState
              title="暂无符合条件的作品，您可以调整筛选条件"
              description="试试减少筛选维度，或浏览全部作品寻找灵感。"
              actionLabel="重置筛选条件"
              onAction={reset}
            />
          </div>
        )}
      </section>
    </div>
  );
}